import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

interface RoleSelectionPageProps {
  showToast: (message: string, type?: 'success' | 'error') => void;
}

export const RoleSelectionPage: React.FC<RoleSelectionPageProps> = ({ showToast }) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [selectedRole, setSelectedRole] = useState<string>('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const roles = [
    {
      id: 'etudiant',
      title: 'Étudiant-e',
      description: 'Je cherche de l\'aide dans mes études',
      icon: 'fa-graduation-cap',
      color: 'blue',
      features: [
        'Rechercher des tuteur-trices',
        'Contacter des répétiteur-trices',
        'Accéder aux profils des tuteur-trices',
        'Système de notation et avis'
      ]
    },
    {
      id: 'tuteur',
      title: 'Tuteur-trice',
      description: 'Je veux aider d\'autres étudiant-es',
      icon: 'fa-chalkboard-teacher',
      color: 'green',
      features: [
        'Créer mon profil de tuteur-trice',
        'Définir mes matières et tarifs',
        'Gérer mes disponibilités',
        'Recevoir des demandes de contact'
      ]
    },
    {
      id: 'both',
      title: 'Les deux',
      description: 'Je veux à la fois aider et être aidé-e',
      icon: 'fa-exchange-alt',
      color: 'purple',
      features: [
        'Toutes les fonctionnalités étudiant-e',
        'Toutes les fonctionnalités tuteur-trice',
        'Profil flexible et évolutif',
        'Communauté d\'entraide complète'
      ]
    }
  ];

  const handleRoleSelect = (roleId: string) => {
    setSelectedRole(roleId);
  };

  const handleContinue = async () => {
    if (!selectedRole) {
      showToast('Veuillez choisir un rôle pour continuer', 'error');
      return;
    }

    if (!user) {
      showToast('Erreur de session. Veuillez vous reconnecter.', 'error');
      navigate('/login');
      return;
    }

    setIsSubmitting(true);

    try {
      // Stocker le choix de rôle dans le localStorage pour la page de création de profil
      localStorage.setItem('selectedRole', selectedRole);
      
      showToast(`Excellent choix ! Créons maintenant votre profil.`, 'success');
      
      // Rediriger vers la page de création de profil appropriée
      if (selectedRole === 'etudiant') {
        navigate('/create-student-profile');
      } else if (selectedRole === 'tuteur') {
        navigate('/create-profile'); // Page existante pour tuteurs
      } else if (selectedRole === 'both') {
        // Pour "les deux", on commence par créer un profil tuteur complet
        // L'utilisateur pourra ensuite basculer entre les rôles
        localStorage.setItem('createBothRoles', 'true');
        navigate('/create-profile');
      }
      
    } catch (err) {
      showToast('Une erreur inattendue s\'est produite', 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const getColorClasses = (color: string, isSelected: boolean) => {
    const baseClasses = isSelected ? 'ring-2 ring-offset-2' : 'hover:shadow-lg';
    
    switch (color) {
      case 'blue':
        return `${baseClasses} ${isSelected ? 'ring-blue-500 border-blue-500' : 'border-gray-200 hover:border-blue-300'}`;
      case 'green':
        return `${baseClasses} ${isSelected ? 'ring-green-500 border-green-500' : 'border-gray-200 hover:border-green-300'}`;
      case 'purple':
        return `${baseClasses} ${isSelected ? 'ring-purple-500 border-purple-500' : 'border-gray-200 hover:border-purple-300'}`;
      default:
        return `${baseClasses} ${isSelected ? 'ring-[#002c46] border-[#002c46]' : 'border-gray-200'}`;
    }
  };

  const getIconColor = (color: string) => {
    switch (color) {
      case 'blue': return 'text-blue-600';
      case 'green': return 'text-green-600';
      case 'purple': return 'text-purple-600';
      default: return 'text-[#002c46]';
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="mx-auto h-16 w-16 bg-[#002c46] rounded-full flex items-center justify-center mb-6">
            <i className="fas fa-user-cog text-white text-2xl"></i>
          </div>
          <h1 className="text-4xl font-bold text-gray-900 mb-4">
            Bienvenue dans HEG-Genève Tutor !
          </h1>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            Pour personnaliser votre expérience, dites-nous comment vous souhaitez utiliser la plateforme
          </p>
        </div>

        {/* Sélection des rôles */}
        <div className="grid md:grid-cols-3 gap-8 mb-12">
          {roles.map((role) => (
            <div
              key={role.id}
              onClick={() => handleRoleSelect(role.id)}
              className={`bg-white rounded-xl shadow-md p-8 cursor-pointer transition-all duration-200 border-2 ${getColorClasses(role.color, selectedRole === role.id)}`}
            >
              <div className="text-center mb-6">
                <div className={`mx-auto h-16 w-16 rounded-full flex items-center justify-center mb-4 ${
                  selectedRole === role.id 
                    ? `bg-${role.color}-100` 
                    : 'bg-gray-100'
                }`}>
                  <i className={`fas ${role.icon} text-2xl ${
                    selectedRole === role.id 
                      ? getIconColor(role.color)
                      : 'text-gray-600'
                  }`}></i>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-2">
                  {role.title}
                </h3>
                <p className="text-gray-600">
                  {role.description}
                </p>
              </div>

              <div className="space-y-3">
                {role.features.map((feature, index) => (
                  <div key={index} className="flex items-center text-sm text-gray-700">
                    <i className={`fas fa-check-circle text-${role.color}-500 mr-3 flex-shrink-0`}></i>
                    <span>{feature}</span>
                  </div>
                ))}
              </div>

              {selectedRole === role.id && (
                <div className="mt-6 text-center">
                  <div className={`inline-flex items-center px-4 py-2 rounded-full text-sm font-medium bg-${role.color}-100 text-${role.color}-800`}>
                    <i className="fas fa-check mr-2"></i>
                    Sélectionné
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Information supplémentaire */}
        <div className="bg-white rounded-xl shadow-md p-6 mb-8">
          <div className="flex items-start">
            <div className="flex-shrink-0">
              <i className="fas fa-info-circle text-[#002c46] text-xl mt-1"></i>
            </div>
            <div className="ml-4">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                Bon à savoir
              </h3>
              <ul className="text-gray-600 space-y-1 text-sm">
                <li>• Vous pourrez toujours modifier votre choix plus tard dans votre profil</li>
                <li>• Si vous choisissez "Les deux", vous pourrez basculer entre les rôles à tout moment</li>
                <li>• Votre profil s'adaptera automatiquement selon vos besoins</li>
                <li>• Tous les services sont gratuits pour les étudiant-es de la HEG</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Bouton de continuation */}
        <div className="text-center">
          <button
            onClick={handleContinue}
            disabled={!selectedRole || isSubmitting}
            className="inline-flex items-center px-8 py-4 bg-[#002c46] text-white font-semibold text-lg rounded-lg hover:bg-[#001a2a] focus:ring-2 focus:ring-[#003d5c] focus:ring-offset-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSubmitting ? (
              <>
                <i className="fas fa-spinner fa-spin mr-3"></i>
                Configuration en cours...
              </>
            ) : (
              <>
                <i className="fas fa-arrow-right mr-3"></i>
                Continuer avec ce rôle
              </>
            )}
          </button>
          
          {!selectedRole && (
            <p className="text-sm text-gray-500 mt-3">
              Choisissez un rôle pour continuer
            </p>
          )}
        </div>

        {/* Aide */}
        <div className="text-center mt-8">
          <p className="text-sm text-gray-600 mb-2">
            Besoin d'aide pour choisir ?
          </p>
          <a
            href="/contact"
            className="text-[#002c46] hover:text-[#001a2a] font-medium text-sm"
          >
            <i className="fas fa-envelope mr-1"></i>
            Contactez notre équipe
          </a>
        </div>
      </div>
    </div>
  );
};
